const fallbackDelay = 60_000;

export function retryAfterMilliseconds(value: unknown, now = Date.now()): number {
  if (typeof value !== 'string' && typeof value !== 'number') return fallbackDelay;
  const text = String(value).trim();
  const delay = /^\d+(?:\.\d+)?$/.test(text) ? Number(text) * 1000 : Date.parse(text) - now;
  // No timer is allocated. Bound malformed/extreme upstream dates to a day,
  // retaining at least one second even for Retry-After: 0.
  return Number.isFinite(delay) ? Math.min(86_400_000, Math.max(1000, delay)) : fallbackDelay;
}

/** JWT refreshes keep the same quota subject; a different login never inherits it. */
export function rateLimitPartition(token: string | null): string {
  if (!token) return 'anonymous';
  try {
    const payload = token.split('.')[1];
    if (payload) {
      const encoded = payload.replaceAll('-', '+').replaceAll('_', '/');
      const claims: unknown = JSON.parse(
        atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')),
      );
      if (
        typeof claims === 'object' &&
        claims !== null &&
        'sub' in claims &&
        typeof claims.sub === 'string'
      )
        return `subject:${claims.sub}`;
    }
  } catch {
    /* Opaque/invalid tokens remain isolated without exposing them in errors. */
  }
  return `opaque:${token}`;
}

export class RateLimitCooldown {
  private readonly deadlines = new Map<string, number>();
  remaining(partition: string, now = Date.now()) {
    const deadline = this.deadlines.get(partition) ?? 0;
    if (deadline <= now) {
      this.deadlines.delete(partition);
      return 0;
    }
    return deadline - now;
  }
  record(partition: string, retryAfter: unknown, now = Date.now()) {
    const until = Math.max(
      this.deadlines.get(partition) ?? 0,
      now + retryAfterMilliseconds(retryAfter, now),
    );
    this.deadlines.delete(partition);
    this.deadlines.set(partition, until);
    if (this.deadlines.size > 8) this.deadlines.delete(this.deadlines.keys().next().value!);
    return until - now;
  }
}

export const apiRateLimitCooldown = new RateLimitCooldown();

export function shouldRetryApiQuery(failureCount: number, error: unknown) {
  return (
    !(typeof error === 'object' && error !== null && 'status' in error && error.status === 429) &&
    failureCount < 1
  );
}
